import { Clear as ClearIcon } from '@mui/icons-material';
import { BhiDialog } from 'src/UIKit/components/Dialog';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  SxProps,
  Tab,
  Tabs,
  Theme,
} from '@mui/material';
import React, { ReactNode } from 'react';
import { FileTabContent } from './FileTabContent';
import { LinkTabContent } from './LinkTabContent';
import { useFileUpload } from './useFileUpload';

export interface FileUploadButtonProps {
  label: ReactNode;
  value: File | string | null | undefined;
  onChange(value: File | string | null): void;
  startIcon?: ReactNode;
  error?: boolean;
  helperText?: ReactNode;
  dropAreaNote?: string;
  linkToFileNote?: string;
  accept?: string;
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'contained' | 'outlined';
  fullWidth?: boolean;
  sx?: { button?: SxProps<Theme> };
}

export const FileUploadButton: React.FC<FileUploadButtonProps> = ({
  label,
  value,
  onChange,
  startIcon,
  error,
  helperText,
  dropAreaNote,
  linkToFileNote,
  accept,
  required,
  disabled,
  size,
  variant = 'contained',
  fullWidth = true,
  sx,
}) => {
  const { 
    isDialogOpen, 
    activeTab, 
    openDialog, 
    closeDialog, 
    handleFileChange, 
    handleLinkChange, 
    handleDelete,
    setActiveTab 
  } = useFileUpload({
    value,
    onChange,
  });

  return (
    <>
      <FormControl
        component="fieldset"
        required={required}
        error={!!error}
        fullWidth={fullWidth}
        sx={sx?.button}
      >
        <ButtonGroup variant={variant} size={size}>
          <Button disabled={disabled} startIcon={startIcon} onClick={openDialog} fullWidth>
            {label}
          </Button>

          {!!value && (
            <Button disabled={disabled} onClick={handleDelete}>
              <ClearIcon />
            </Button>
          )}
        </ButtonGroup>

        {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
      </FormControl>

      <BhiDialog
        title="Attach"
        isOpen={isDialogOpen}
        onClose={closeDialog}
        paperSx={{ width: '600px', minWidth: '600px' }}
      >
        <Box sx={{ padding: '0 20px 20px' }}>
          <Tabs value={activeTab} onChange={(_, tab) => setActiveTab(tab)} sx={{ marginBottom: '20px' }}>
            <Tab label="Upload" value="file" key="file" />
            <Tab label="Link to file" value="link" key="link" />
          </Tabs>

          {activeTab === 'file' && (
            <FileTabContent 
              dropAreaNote={dropAreaNote} 
              accept={accept} 
              onSubmit={handleFileChange} 
            />
          )}

          {activeTab === 'link' && (
            <LinkTabContent 
              linkToFileNote={linkToFileNote} 
              value={value} 
              onSubmit={handleLinkChange} 
            />
          )}
        </Box>
      </BhiDialog>
    </>
  );
};
